import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Modal, Table, Upload } from 'components/Ui'
import { updateApiRecord, deleteApiRecord, uploadApiCustomersRecords } from 'services/CustomersService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { companyStatus } from 'components/helper'
import { Field, Form, Formik } from 'formik'
import { Button } from 'components/Form'
import * as Yup from 'yup'
import { getCustomersData } from 'store/data/dataSlice'
injectReducer('customers', reducer)

const validationSchema = Yup.object().shape({
    file: Yup.string().required('file is required'),
})

export default function Customers() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(state => state.customers.data)
    const { company } = useSelector(state => state.filterData)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        company,
    ])

    const fetchData = () => dispatch(getData({ ...tableData, company: company }))

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleStatus = (id, data) => {
        delete data.image
        updateApiRecord(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row.company.code,
            },
            {
                field: 'customer_code',
                header: 'Customer Code',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'customer_type',
                header: 'Customer Type',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'contact1',
                header: 'Contact Name',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'contact1Tel1',
                header: 'Contact Mobile Number',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'country',
                header: 'Country',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'province',
                header: 'State',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'city',
                header: 'City',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'machship_code',
                header: 'Machship Code',
                className: 'min-w-[150px]',
                hide: false,
            },
            {
                field: 'status',
                header: 'Status',
                hide: false,
                renderCell: row => {
                    return checkPermission('customers', 'edit') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, { ...row, status: row.status === 'active' ? 'inactive' : 'active' })
                            }}
                        >
                            {companyStatus(row.status)}
                        </ConfirmationModal>
                    ) : (
                        companyStatus(row.status)
                    )
                },
            },
            {
                field: 'action',
                header: 'Action',
                className: 'px-4',
                hide: false,
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('customers', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link to={`/company-management/customers/${row.id}/edit`} className="hover:text-primary">
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('customers', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={'Are you sure you want to delete this record!'}
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            company,
        ]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Customers</h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                upload={true}
                datas={data}
                columns={columns}
                add_new={checkPermission('customers', 'create') ? '/company-management/customers/create' : ''}
                pagingData={tableData}
                onChange={e => dispatch(setTableData(e))}
                onUpload={() => setOpen(true)}
            />
            <Modal
                show={open}
                onClose={() => {
                    setOpen(false)
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">{'Import  Customers'}</h2>
                    <p>Automatically create new Customers by uploading your standard file</p>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={{ file: '' }}
                                validationSchema={validationSchema}
                                onSubmit={values => {
                                    setOpen(false)
                                    uploadApiCustomersRecords(values)
                                        .then(() => {
                                            dispatch(getCustomersData())
                                            fetchData()
                                            Success('File Uploaded Successfully!')
                                        })
                                        .catch(error => console.log('Error: ', error))
                                }}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                            <Field name="file">
                                                {({ field, form }) => (
                                                    <Upload
                                                        onFilesSelected={files => form.setFieldValue(field.name, files[0])}
                                                        error={errors.file && touched.file}
                                                        message={errors.file}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-[#F05540] border border-[#F05540] dark:text-[#F05540] dark:border-[#F05540]"
                                                onClick={() => setOpen(false)}
                                                type="button"
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
